import { useEffect, useRef, useState } from "react";
import vigilantImg from "../../../assets/team/vigilant.jpg";
import henryImg from "../../../assets/team/henry.jpg";
import jonasImg from "../../../assets/team/jonas.jpg";
import obedImg from "../../../assets/team/obed.jpg";
import benedictImg from "../../../assets/team/benedict.jpg";
import lotharImg from "../../../assets/team/lothar.jpg";
import desmondImg from "../../../assets/team/desmond.jpg";

interface TeamMember {
  name: string;
  role: string;
  img: string;
  imgStyle: React.CSSProperties;
  socials: { linkedin: string };
  tag: string;
}

const team: TeamMember[] = [
  {
    name: "Vigilant Kwao",
    role: "Chief Executive Officer",
    img: vigilantImg,
    imgStyle: { objectPosition: "center top" },
    socials: { linkedin: "https://www.linkedin.com/in/vigilant-kwao" },
    tag: "Leadership/Eng",
  },
  {
    name: "Henry Amankwah-Famiyeh",
    role: "Chief Information Officer",
    img: henryImg,
    imgStyle: { objectPosition: "center 20%", transform: "scale(0.92)", transformOrigin: "center 20%" },
    socials: { linkedin: "https://www.linkedin.com/in/henry-famiyeh-086633300/" },
    tag: "Engineering",
  },
  {
    name: "Jonas Botsio",
    role: "Chief Technology Officer",
    img: jonasImg,
    imgStyle: { objectPosition: "center top" },
    socials: { linkedin: "https://www.linkedin.com/in/jonas-botsio" },
    tag: "Engineering",
  },
  {
    name: "Obed Nana Kwame Offei",
    role: "Chief Operating Officer",
    img: obedImg,
    imgStyle: { objectPosition: "center 15%" },
    socials: { linkedin: "#" },
    tag: "Operations",
  },
  {
    name: "Benedict Jephtha Quaye",
    role: "Chief Marketing Officer",
    img: benedictImg,
    imgStyle: { objectPosition: "center 15%" },
    socials: { linkedin: "#" },
    tag: "Marketing",
  },
  {
    name: "Lother Abbey-Hart",
    role: "Chief Brand Officer",
    img: lotharImg,
    imgStyle: { objectPosition: "center top" },
    socials: { linkedin: "#" },
    tag: "Brand",
  },
  {
    name: "Desmond Bokor",
    role: "Frontend Developer",
    img: desmondImg,
    imgStyle: { objectPosition: "center 15%" },
    socials: { linkedin: "https://www.linkedin.com/in/desmond-bokor-7a6722204/" },
    tag: "Software Developer",
  },
];

const tagColors: Record<string, string> = {
  Leadership: "bg-amber-50 text-amber-700",
  "Leadership/Eng": "bg-amber-50 text-amber-700",
  Engineering: "bg-emerald-50 text-emerald-700",
  Frontend: "bg-sky-50 text-sky-700",
  Operations: "bg-teal-50 text-teal-700",
  Brand: "bg-pink-50 text-pink-700",
  Marketing: "bg-orange-50 text-orange-700",
};

export default function Team() {
  const [visible, setVisible] = useState(false);
  const [hovered, setHovered] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="team" ref={sectionRef} className="py-24 md:py-36 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className={`mb-16 transition-all duration-700 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-4 block">The People</span>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-950 leading-tight">
              Meet the<br />
              <span className="italic font-light text-gray-400">Team Behind It</span>
            </h2>
            <p className="text-gray-500 max-w-sm text-base leading-relaxed">
              A tight-knit crew of builders, strategists, and innovators — passionate about crafting solutions that actually make a difference.
            </p>
          </div>
        </div>

        {/* Team Grid */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-6">
          {team.map((member, i) => (
            <div
              key={i}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              className={`group relative bg-gray-50 rounded-2xl overflow-hidden border border-gray-100 transition-all duration-500 cursor-default ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
              }`}
              style={{ transitionDelay: `${i * 80}ms` }}
            >
              {/* Image */}
              <div className="relative w-full h-64 sm:h-72 overflow-hidden">
                <img
                  src={member.img}
                  alt={member.name}
                  className={`w-full h-full object-cover transition-transform duration-700 ${hovered === i ? "scale-105" : "scale-100"}`}
                  style={member.imgStyle}
                />
                <div className={`absolute inset-0 bg-gradient-to-t from-gray-950/70 via-transparent to-transparent transition-opacity duration-300 ${hovered === i ? "opacity-100" : "opacity-0"}`}></div>

                {/* Social links on hover */}
                <div className={`absolute bottom-4 left-0 right-0 flex justify-center transition-all duration-300 ${hovered === i ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
                  <a
                    href={member.socials.linkedin}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/40 transition-colors"
                  >
                    <i className="ri-linkedin-line text-white text-sm"></i>
                  </a>
                </div>
              </div>

              {/* Info */}
              <div className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h3 className="font-bold text-gray-950 text-sm sm:text-base">{member.name}</h3>
                    <p className="text-gray-500 text-xs sm:text-sm">{member.role}</p>
                  </div>
                  <span className={`text-[10px] sm:text-xs font-medium px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${tagColors[member.tag]}`}>
                    {member.tag}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom note */}
        <div className={`mt-14 text-center transition-all duration-700 delay-500 ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <p className="text-gray-400 text-sm">
            Want to join the team?{" "}
            <button
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="text-gray-950 font-semibold underline underline-offset-2 cursor-pointer hover:opacity-70 transition-opacity"
            >
              Reach out to us
            </button>
          </p>
        </div>
      </div>
    </section>
  );
}
